import { useEffect, useState, useMemo } from 'react';
import { apiFetch } from '../../utils/api';
import UserTable from '../../components/admin/UserTable';
import {
  FaUserPlus,
  FaSearch,
  FaTimes,
  FaShieldAlt,
  FaUsers,
  FaUserCheck,
  FaSave,
  FaLock,
} from 'react-icons/fa';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Form states
  const [addFormData, setAddFormData] = useState({
    name: '',
    email: '',
    password: '',
    isAdmin: false,
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    password: '',
    isAdmin: false,
  });

  const [formSubmitting, setFormSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  const currentAdmin = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userInfo') || 'null');
    } catch {
      return null;
    }
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/users/all');
      if (!res.ok) {
        throw new Error('Failed to load users');
      }
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Filtered users by search
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  // Stats
  const adminCount = useMemo(() => users.filter((u) => u.isAdmin).length, [users]);
  const standardCount = useMemo(() => users.filter((u) => !u.isAdmin).length, [users]);

  // Open Add Modal
  const handleOpenAddModal = () => {
    setAddFormData({
      name: '',
      email: '',
      password: '',
      isAdmin: false,
    });
    setModalError('');
    setAddModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      password: '', // Blank by default; only changed if typed
      isAdmin: Boolean(user.isAdmin),
    });
    setModalError('');
    setEditModalOpen(true);
  };

  // Submit Add User
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setModalError('');

    if (!addFormData.name.trim() || !addFormData.email.trim() || !addFormData.password) {
      setModalError('Please fill in all required fields.');
      return;
    }

    if (addFormData.password.length < 6) {
      setModalError('Password must be at least 6 characters long.');
      return;
    }

    try {
      setFormSubmitting(true);
      const res = await apiFetch('/api/users', {
        method: 'POST',
        body: JSON.stringify({
          name: addFormData.name.trim(),
          email: addFormData.email.trim(),
          password: addFormData.password,
          isAdmin: addFormData.isAdmin,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to create user');
      }

      setSuccess('User created successfully!');
      setAddModalOpen(false);
      await loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setModalError(err.message || 'Failed to create user');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Submit Edit User
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setModalError('');

    if (!editFormData.name.trim() || !editFormData.email.trim()) {
      setModalError('Name and Email are required.');
      return;
    }

    if (editFormData.password && editFormData.password.length < 6) {
      setModalError('New password must be at least 6 characters long.');
      return;
    }

    try {
      setFormSubmitting(true);
      const payload = {
        name: editFormData.name.trim(),
        email: editFormData.email.trim(),
        isAdmin: editFormData.isAdmin,
      };

      if (editFormData.password) {
        payload.password = editFormData.password;
      }

      const res = await apiFetch(`/api/users/${editingUser._id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to update user');
      }

      setSuccess(`User "${editFormData.name}" updated successfully!`);
      setEditModalOpen(false);
      setEditingUser(null);
      await loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setModalError(err.message || 'Failed to update user');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Delete User
  const handleDeleteUser = async (user) => {
    if (user._id === currentAdmin?._id) {
      alert('You cannot delete your own logged-in admin account.');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete user "${user.name}" (${user.email})? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const res = await apiFetch(`/api/users/${user._id}`, { method: 'DELETE' });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to delete user');
      }
      setSuccess(`User "${user.name}" removed successfully.`);
      await loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Unable to delete user');
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-3xl border border-slate-200/90 bg-white p-6 shadow-2xs">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
            Team & User Management
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Create, edit, assign roles, and manage permissions for all administrative and standard accounts.
          </p>
        </div>

        <button
          type="button"
          onClick={handleOpenAddModal}
          className="btn-primary py-2.5 px-5 rounded-xl text-xs font-bold inline-flex items-center gap-2 shadow-sm shrink-0 cursor-pointer"
        >
          <FaUserPlus className="text-xs" />
          <span>Add New User</span>
        </button>
      </div>

      {/* Notifications */}
      {success && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-xs font-semibold text-emerald-800 shadow-2xs">
          {success}
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-xs font-semibold text-red-700 shadow-2xs">
          {error}
        </div>
      )}

      {/* Stats and Filter Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Pills */}
        <div className="flex items-center gap-2.5 text-xs">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-2xs">
            <FaUsers className="text-blue-500" />
            <span className="text-slate-500">Total:</span>
            <strong className="text-slate-900">{users.length}</strong>
          </div>

          <div className="flex items-center gap-2 rounded-xl border border-amber-200/80 bg-amber-50/60 px-3 py-2 shadow-2xs">
            <FaShieldAlt className="text-amber-600" />
            <span className="text-amber-800">Admins:</span>
            <strong className="text-amber-950">{adminCount}</strong>
          </div>

          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 shadow-2xs">
            <FaUserCheck className="text-slate-500" />
            <span className="text-slate-500">Users:</span>
            <strong className="text-slate-800">{standardCount}</strong>
          </div>
        </div>

        {/* Search */}
        <div className="relative w-full md:w-72">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-8 text-xs text-slate-800 shadow-2xs placeholder:text-slate-400 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100 transition"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
            >
              <FaTimes className="text-[10px]" />
            </button>
          )}
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-2xl bg-white border border-slate-200" />
          ))}
        </div>
      ) : (
        <UserTable
          users={filteredUsers}
          onEdit={handleOpenEditModal}
          onDelete={handleDeleteUser}
          currentUserId={currentAdmin?._id}
        />
      )}

      {/* ADD USER MODAL */}
      {addModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 backdrop-blur-2xs p-4">
          <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                  <FaUserPlus className="text-sm" />
                </div>
                <h3 className="font-bold text-slate-900 text-sm">Add New User</h3>
              </div>
              <button
                type="button"
                onClick={() => setAddModalOpen(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <FaTimes className="text-sm" />
              </button>
            </div>

            <form onSubmit={handleAddSubmit} className="p-6 space-y-4 text-xs">
              {modalError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-700 font-medium">
                  {modalError}
                </div>
              )}

              <div>
                <label className="block font-bold text-slate-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={addFormData.name}
                  onChange={(e) =>
                    setAddFormData((p) => ({ ...p, name: e.target.value }))
                  }
                  placeholder="e.g. John Doe"
                  required
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs text-slate-800 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={addFormData.email}
                  onChange={(e) =>
                    setAddFormData((p) => ({ ...p, email: e.target.value }))
                  }
                  placeholder="e.g. user@finegal.com"
                  required
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs text-slate-800 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Password</label>
                <input
                  type="password"
                  value={addFormData.password}
                  onChange={(e) =>
                    setAddFormData((p) => ({ ...p, password: e.target.value }))
                  }
                  placeholder="Minimum 6 characters"
                  required
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs text-slate-800 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div className="pt-1">
                <label className="block font-bold text-slate-700 mb-1">User Role</label>
                <select
                  value={addFormData.isAdmin ? 'admin' : 'user'}
                  onChange={(e) =>
                    setAddFormData((p) => ({
                      ...p,
                      isAdmin: e.target.value === 'admin',
                    }))
                  }
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs text-slate-800 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="user">Standard User (View & Content)</option>
                  <option value="admin">Administrator (Full Control)</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setAddModalOpen(false)}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="btn-primary rounded-xl px-5 py-2 text-xs font-bold inline-flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                >
                  <FaUserPlus className="text-xs" />
                  <span>{formSubmitting ? 'Creating...' : 'Create User'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {editModalOpen && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 backdrop-blur-2xs p-4">
          <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <FaLock className="text-xs" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Edit User Details</h3>
                  <p className="text-[11px] text-slate-400">Update account credentials and role</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <FaTimes className="text-sm" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-4 text-xs">
              {modalError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-700 font-medium">
                  {modalError}
                </div>
              )}

              <div>
                <label className="block font-bold text-slate-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData((p) => ({ ...p, name: e.target.value }))
                  }
                  required
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs text-slate-800 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) =>
                    setEditFormData((p) => ({ ...p, email: e.target.value }))
                  }
                  required
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs text-slate-800 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  Reset Password <span className="font-normal text-slate-400">(leave blank to keep current)</span>
                </label>
                <input
                  type="password"
                  value={editFormData.password}
                  onChange={(e) =>
                    setEditFormData((p) => ({ ...p, password: e.target.value }))
                  }
                  placeholder="New password (optional)"
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs text-slate-800 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div className="pt-1">
                <label className="block font-bold text-slate-700 mb-1">User Role</label>
                <select
                  value={editFormData.isAdmin ? 'admin' : 'user'}
                  onChange={(e) =>
                    setEditFormData((p) => ({
                      ...p,
                      isAdmin: e.target.value === 'admin',
                    }))
                  }
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs text-slate-800 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="user">Standard User</option>
                  <option value="admin">Administrator (Full Control)</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditModalOpen(false)}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="btn-primary rounded-xl px-5 py-2 text-xs font-bold inline-flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                >
                  <FaSave className="text-xs" />
                  <span>{formSubmitting ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
